/**
 * <click-area> web component.
 *
 * Användning:
 *
 *   <click-area class="group border-2" data-appear="fadeInUp">
 *       <article>
 *           <h3>
 *               <a data-click-area-link href="/produkt/x">Produkt X</a>
 *           </h3>
 *           <p>Beskrivning...</p>
 *       </article>
 *   </click-area>
 *
 * Allt innehåll inuti blir klickbart. Den första <a data-click-area-link>
 * (eller om den saknas: första <a> som hittas) används som "huvudlänk".
 *
 *  - Stöder vänsterklick + cmd/ctrl-klick (öppnar i ny flik).
 *  - Stöder mellanklick (öppnar i ny flik).
 *  - Hoppar över klick som hamnat på andra länkar/knappar/formfält
 *    inuti komponenten (de fungerar som vanligt).
 *  - Hoppar över klick efter textmarkering.
 *  - Tangentbordsfokus och tillgänglighet ärvs från den underliggande <a>:n.
 */

class ClickArea extends HTMLElement {
    connectedCallback() {
        // Vänta tills DOM:en inuti är klar (för server-renderat innehåll
        // är den klar direkt; för dynamisk insättning täcker MutationObservern).
        this._upgrade();

        this._observer = new MutationObserver(() => this._upgrade());
        this._observer.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
        this._observer?.disconnect();
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('auxclick', this._onAuxClick);
        this.removeEventListener('mousedown', this._onMouseDown);
    }

    _upgrade() {
        const link = this._findLink();
        if (!link) {
            this.style.cursor = '';
            return;
        }
        this.style.cursor = 'pointer';

        // Bind events bara en gång
        if (!this._bound) {
            this._onClick = this._onClick.bind(this);
            this._onAuxClick = this._onAuxClick.bind(this);
            this._onMouseDown = this._onMouseDown.bind(this);

            this.addEventListener('mousedown', this._onMouseDown);
            this.addEventListener('click', this._onClick);
            this.addEventListener('auxclick', this._onAuxClick);
            this._bound = true;
        }
    }

    _findLink() {
        return (
            this.querySelector('[data-click-area-link]') ||
            this.querySelector('a[href]')
        );
    }

    _shouldIgnore(event) {
        const interactive = event.target.closest(
            'a, button, input, textarea, select, label, [role="button"], [data-click-area-ignore]'
        );
        if (!interactive) return false;

        // Klick direkt på huvudlänken – låt webbläsaren navigera nativt.
        // Annars triggar preventDefault() + link.click() en rekursiv loop
        // som gör att länken aldrig navigerar.
        if (interactive === this._findLink()) return true;

        // Övriga interaktiva element ska inte heller triggra wrap-klicket.
        return true;
    }

    _onMouseDown(event) {
        this._mouseDownX = event.clientX;
        this._mouseDownY = event.clientY;
    }

    _onClick(event) {
        if (this._shouldIgnore(event)) return;

        // Om användaren markerat text - öppna inte länken
        if (this._isTextSelection(event)) return;

        const link = this._findLink();
        if (!link) return;

        // Förhindra default-bubblan
        event.preventDefault();

        if (event.metaKey || event.ctrlKey || event.shiftKey) {
            window.open(link.href, '_blank', 'noopener');
        } else {
            link.click();
        }
    }

    _onAuxClick(event) {
        // Mellanklick = öppna i ny flik
        if (event.button !== 1) return;
        if (this._shouldIgnore(event)) return;

        const link = this._findLink();
        if (!link) return;

        event.preventDefault();
        window.open(link.href, '_blank', 'noopener');
    }

    _isTextSelection(event) {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) return false;

        // Om text faktiskt är markerad och muspekaren rört sig
        const dx = Math.abs(event.clientX - (this._mouseDownX ?? event.clientX));
        const dy = Math.abs(event.clientY - (this._mouseDownY ?? event.clientY));
        return dx > 4 || dy > 4;
    }
}

if (!customElements.get('click-area')) {
    customElements.define('click-area', ClickArea);
}

export default ClickArea;
